import { useCallback } from 'react';
import useSWR from 'swr';

import { carsViolationsRetrieve } from '~/data/generated/endpoints/cars/cars';
import { parseApiResponse } from '~/data/parseApiResponse';
import { violationsResponseSchema } from '~/data/schemas/car';

// The Violation shape is owned by `~/data/schemas/car` (AMP-585), which derives
// it from the generated `CarsViolationsRetrieveResponse`. It used to be
// re-declared here as a hand-written interface plus an `as unknown as` cast at
// the boundary, which is exactly what let the two drift: the local union typed
// `status` as lowercase while the API emits "Open"/"Overdue"/"Resolved", and
// typed `location`/`responsibility` as required non-null while the model has
// both `null=True, blank=True`. Re-export the single source of truth instead so
// the components that import `Violation` from this module keep working.
//
// This also carries CX-416's reason for `code` being optional: LOLER (UK)
// defects carry no code, so the model's `code` is `blank=True`, DRF marks it
// not-required, and the generated schema types it optional. That now flows
// through `~/data/schemas/car` rather than being restated here.
export type { Violation } from '~/data/schemas/car';

const violationsFetcher = async (carId: string) => {
  const { data } = await carsViolationsRetrieve(carId);
  return parseApiResponse(violationsResponseSchema, data, 'CarViolations');
};

export const useViolations = (carId: string | undefined) => {
  const { data, error, isLoading, mutate } = useSWR(
    carId ? ['violation', carId] : null,
    () => (carId ? violationsFetcher(carId) : null),
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
    },
  );

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return {
    violations: data?.results ?? [],
    statistics: data?.statistics,
    isLoading,
    error,
    refresh,
  };
};
